// OwnersDataSection.jsx
import React from "react";
import { Info, CheckCircle2, AlertCircle, Users } from "lucide-react";

/** --- Visuals (matches your other sections) --- */
const Karta = ({ children, style }) => (
  <div
    style={{
      border: "1px solid #e6e9ee",
      borderRadius: 16,
      padding: 16,
      background: "#fff",
      boxShadow: "0 16px 48px rgba(16,24,40,0.16), 0 8px 22px rgba(16,24,40,0.12)",
      ...style,
    }}
  >
    {children}
  </div>
);

const Sekcja = ({ title, icon: Icon = Info, children }) => (
  <Karta>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
      {Icon ? <Icon size={18} color="#0b74e5" /> : null}
      <h3 style={{ margin: 0, fontSize: 18, color: "#0b74e5", textDecorationThickness: "2px", textUnderlineOffset: 4 }}>
        {title}
      </h3>
    </div>
    {children}
  </Karta>
);

const StatusCard = ({ title, status, tone = "info", subtitle }) => {
  const tones = {
    ok:   { top: "#22c55e", icon: "#22c55e", text: "#111827", sub: "#6b7280", border: "#e6f6ec", Icon: CheckCircle2 },
    bad:  { top: "#ef4444", icon: "#ef4444", text: "#111827", sub: "#6b7280", border: "#fdecec", Icon: AlertCircle  },
    warn: { top: "#f59e0b", icon: "#f59e0b", text: "#111827", sub: "#6b7280", border: "#fef3c7", Icon: AlertCircle  },
    info: { top: "#3b82f6", icon: "#3b82f6", text: "#111827", sub: "#6b7280", border: "#dbeafe", Icon: Info        },
  };
  const c = tones[tone] || tones.info;
  const IconComp = c.Icon;

  return (
    <div
      style={{
        border: `1px solid ${c.border}`,
        borderRadius: 14,
        background: "#fff",
        boxShadow: "0 6px 24px rgba(16,24,40,0.06)",
        overflow: "hidden",
        display: "grid",
      }}
    >
      <div style={{ height: 6, background: c.top }} />
      <div style={{ display: "grid", gridTemplateColumns: "56px 1fr", gap: 12, padding: 14 }}>
        <div
          style={{
            display: "grid",
            placeItems: "center",
            height: 56,
            width: 56,
            borderRadius: "999px",
            border: `2px solid ${c.icon}`,
            color: c.icon,
          }}
        >
          <IconComp size={28} />
        </div>
        <div style={{ display: "grid", gap: 6 }}>
          <div style={{ fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase", color: "#6b7280", fontWeight: 700 }}>
            {title}
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: c.text }}>{status}</div>
          {subtitle ? <div style={{ fontSize: 12, color: c.sub }}>{subtitle}</div> : null}
        </div>
      </div>
    </div>
  );
};

/** --- Helpers --- */
const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : s);

const isUnknown = (v) => {
  const x = String(v ?? "").trim().toLowerCase();
  return x === "-" || x === "" || x.startsWith("brak informacji");
};

const isDateLike = (val) => /^\d{4}-\d{2}-\d{2}(?:t.*)?$/i.test(String(val ?? "").trim());
const fmtDate = (val) => {
  try {
    const d = new Date(String(val));
    return isNaN(d) ? String(val) : d.toLocaleDateString("pl-PL");
  } catch {
    return String(val ?? "—");
  }
};

const fmtInt = (v) => {
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n.toLocaleString("pl-PL") : String(v);
};

function mapYesNoTone(raw) {
  const v = String(raw ?? "").trim().toLowerCase();
  if (v === "tak") return { tone: "ok", display: "Tak" };
  if (v === "nie") return { tone: "bad", display: "Nie" };
  if (v === "nie dotyczy") return { tone: "info", display: "Nie dotyczy" };
  return { tone: "info", display: cap(raw) || "—" };
}

function mapRiskYesNo(raw) {
  // For risk-like flags (PEP/sanctions): Tak = bad, Nie = ok
  const v = String(raw ?? "").trim().toLowerCase();
  if (v === "tak") return { tone: "bad", display: "Tak" };
  if (v === "nie") return { tone: "ok", display: "Nie" };
  if (v === "nie dotyczy") return { tone: "info", display: "Nie dotyczy" };
  return { tone: "info", display: cap(raw) || "—" };
}

function mapOwnersField(key, obj) {
  const title = obj?.label_short || key;
  const long = obj?.label_long || "";
  const raw = obj?.value == null ? "" : String(obj.value).trim();

  // Unknowns → neutral status, show original note as subtitle
  if (isUnknown(raw)) {
    return { title, status: "—", tone: "info", subtitle: long || obj?.value || "" };
  }

  // owner_change: date or plain text
  if (key === "owner_change") {
    const status = isDateLike(raw) ? fmtDate(raw) : cap(raw);
    return { title, status, tone: "info", subtitle: long };
  }

  // owner_age: descriptive text (years or note)
  if (key === "owner_age") {
    return { title, status: cap(raw), tone: "info", subtitle: long };
  }

  // owner_single_person: yes/no (neutral scheme — tweak if you want warn for "Tak")
  if (key === "owner_single_person") {
    const { tone, display } = mapYesNoTone(raw);
    return { title, status: display, tone, subtitle: long };
  }

  // owner_real_beneficiary: yes/no (knowing beneficiary is 'Tak' -> ok)
  if (key === "owner_real_beneficiary") {
    const v = raw.toLowerCase();
    if (v === "tak") return { title, status: "Tak", tone: "ok", subtitle: long };
    if (v === "nie") return { title, status: "Nie", tone: "warn", subtitle: long };
    return { title, status: cap(raw), tone: "info", subtitle: long };
  }

  // owner_related_subjects: numeric count → thresholds
  if (key === "owner_related_subjects") {
    const n = Number(raw.replace(",", "."));
    let tone = "ok";
    if (Number.isFinite(n)) {
      if (n === 0) tone = "ok";
      else if (n <= 3) tone = "info";
      else if (n <= 10) tone = "warn";
      else tone = "bad";
      return { title, status: fmtInt(n), tone, subtitle: long };
    }
    return { title, status: cap(raw), tone: "info", subtitle: long };
  }

  // owner_pep / owner_saction_list: Tak → bad, Nie → ok, unknown → neutral
  if (key === "owner_pep" || key === "owner_saction_list") {
    const { tone, display } = mapRiskYesNo(raw);
    return { title, status: display, tone, subtitle: long };
  }

  // default
  return { title, status: cap(raw), tone: "info", subtitle: long };
}

/** --- Section --- */
export default function OwnersDataSection({ data = {} }) {
  const entries = Object.entries(data).map(([key, obj]) => ({ key, ...mapOwnersField(key, obj) }));

  if (entries.length === 0) {
    return (
      <Sekcja title="Właściciele">
        <em>Brak danych.</em>
      </Sekcja>
    );
  }

  return (
    <Sekcja title="Właściciele" icon={Users}>
      <style>{`
        .ownersGrid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 16px;
        }
        @media (max-width: 1280px) { .ownersGrid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
        @media (max-width: 1024px) { .ownersGrid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        @media (max-width: 640px)  { .ownersGrid { grid-template-columns: 1fr; } }
      `}</style>

      <div className="ownersGrid">
        {entries.map(({ key, title, status, tone, subtitle }) => (
          <StatusCard key={key} title={title} status={status} tone={tone} subtitle={subtitle} />
        ))}
      </div>
    </Sekcja>
  );
}
